import { useQuery } from '@tanstack/react-query';

import { fetchJsonOrThrow } from '@/utils/fetch';
import { getPortfolioRefreshNonce } from '@/utils/portfolioRefresh';
import { retryNetworkOrServerError } from '@/utils/queryClient';
import { routes } from '@/utils/routes';
import type { WalletPortfolio } from '@/utils/zerion/types';

interface UseWalletPortfolioParams {
  evmAddress?: string;
  solanaAddress?: string;
  enabled?: boolean;
}

const fetchPortfolio = (url: string) => fetchJsonOrThrow<WalletPortfolio>(url);

export const walletPortfolioQueryKey = (evmAddress?: string, solanaAddress?: string) =>
  ['wallet-portfolio', evmAddress, solanaAddress] as const;

function buildUrl(
  { evmAddress, solanaAddress }: UseWalletPortfolioParams,
  refreshNonce: string,
): string | null {
  if (!evmAddress && !solanaAddress) return null;
  const params = new URLSearchParams();
  if (evmAddress) params.set('evmAddress', evmAddress);
  if (solanaAddress) params.set('solanaAddress', solanaAddress);
  if (refreshNonce) params.set('_refresh', refreshNonce);
  return `${routes.api.WALLET_PORTFOLIO}?${params.toString()}`;
}

export function useWalletPortfolio({
  evmAddress,
  solanaAddress,
  enabled = true,
}: UseWalletPortfolioParams) {
  const params = { evmAddress, solanaAddress };
  const queryEnabled = enabled && buildUrl(params, '') !== null;

  return useQuery<WalletPortfolio, Error>({
    queryKey: walletPortfolioQueryKey(evmAddress, solanaAddress),
    queryFn: () => fetchPortfolio(buildUrl(params, getPortfolioRefreshNonce())!),
    enabled: queryEnabled,
    staleTime: 60_000,
    retry: retryNetworkOrServerError,
    refetchInterval: 60_000,
    // Opt back in to focus refetches (disabled globally) so the portfolio
    // refreshes when the user returns to the tab. `staleTime` above keeps
    // rapid re-focuses from thrashing the API.
    refetchOnWindowFocus: true,
  });
}
